import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { ExternalLink } from "lucide-react";

function isExternal(url) { return /^https?:\/\//i.test(url || ""); }

function CTA({ label, link, style }) {
  if (!label) return null;
  const cls = "inline-flex items-center gap-1.5 rounded-full font-bold px-4 py-1.5 text-sm shadow-sm hover:scale-[1.03] transition-transform";
  if (isExternal(link)) return <a href={link} target="_blank" rel="noopener noreferrer" className={cls} style={style}>{label} <ExternalLink className="w-3.5 h-3.5" /></a>;
  return <Link to={link || "#"} className={cls} style={style}>{label}</Link>;
}

/**
 * Renders all custom banners for a given placement (fetched once, filtered).
 * If `placement === "header_top"` it renders a compact sticky top strip.
 */
export default function BannerSlot({ placement }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/banners").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const list = items.filter((b) => b.placement === placement && b.enabled !== false);
  if (list.length === 0) return null;

  if (placement === "header_top") {
    return (
      <div data-testid="banner-slot-header_top">
        {list.map((b) => (
          <div key={b.banner_id} data-testid={`custom-banner-${b.banner_id}`} className="w-full py-2 px-4 text-sm font-bold text-center" style={{ background: b.bg_color, color: b.text_color }}>
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
              {b.badge && <span className="uppercase tracking-wider text-xs opacity-90">{b.badge}</span>}
              <span>{b.title}{b.description ? ` — ${b.description}` : ""}</span>
              {b.button_label && <CTA label={b.button_label} link={b.button_link} style={{ background: b.button_bg, color: b.button_text }} />}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Generic block placement (explore_top, home_* etc)
  return (
    <div data-testid={`banner-slot-${placement}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
      {list.map((b) => {
        const style = b.image_path
          ? { backgroundImage: `linear-gradient(135deg, ${b.bg_color}dd, ${b.bg_color}b3), url(${fileUrl(b.image_path)})`, backgroundSize: "cover", backgroundPosition: "center", color: b.text_color }
          : { background: b.bg_color, color: b.text_color };
        return (
          <div key={b.banner_id} data-testid={`custom-banner-${b.banner_id}`} className="rounded-3xl overflow-hidden p-6 sm:p-8 shadow-md" style={style}>
            <div className="grid md:grid-cols-5 gap-4 items-center">
              <div className="md:col-span-3">
                {b.badge && <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]" style={{ background: `${b.accent_color}33`, color: b.text_color, border: `1px solid ${b.accent_color}55` }}>{b.badge}</span>}
                <h3 className="mt-2 font-heading font-black text-xl sm:text-2xl">{b.title} {b.highlight && <span style={{ color: b.accent_color }}>{b.highlight}</span>}</h3>
                {b.description && <p className="mt-2 opacity-90 text-sm">{b.description}</p>}
              </div>
              {b.button_label && (
                <div className="md:col-span-2 flex md:justify-end">
                  <CTA label={b.button_label} link={b.button_link} style={{ background: b.button_bg, color: b.button_text }} />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
